import imageSizes from '~/constants/imageSizes'
import type { Definition } from '~/types'

export const EventAgendaSectionItem: Definition = {
  contentType: 'EventAgendaSectionItem',
  fields: [
    {
      id: 'time',
      name: 'Time',
      type: 'Symbol'
    },
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol'
    },
    {
      id: 'speaker',
      name: 'Speaker',
      type: 'Symbol'
    },
    {
      id: 'description',
      name: 'Description',
      type: 'RichText'
    }
  ]
}

export const EventAgendaSection = {
  contentType: 'EventAgendaSection',
  fields: [
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol'
    },
    {
      id: 'agendaSectionItems',
      name: 'Agenda section items',
      type: 'Array',
      subType: 'EventAgendaSectionItem',
      validations: [{ size: { min: 1, max: 10 } }]
    }
  ]
}

export const EventBlockAgenda = {
  contentType: 'EventBlockAgenda',
  fields: [
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol'
    },
    {
      id: 'description',
      name: 'Description',
      type: 'RichText'
    },
    {
      id: 'agendaSections',
      name: 'Agenda sections',
      type: 'Array',
      subType: 'EventAgendaSection',
      validations: [{ size: { min: 1, max: 10 } }]
    },
    {
      id: 'theme',
      name: 'Theme',
      type: 'GlobalDataTheme'
    }
  ]
}

export const EventLocation = {
  contentType: 'EventLocation',
  fields: [
    {
      id: 'venue',
      name: 'Venue',
      type: 'Symbol'
    },
    {
      id: 'city',
      name: 'City',
      type: 'Symbol'
    },
    {
      id: 'addressLine1',
      name: 'Address line 1',
      type: 'Symbol'
    },
    {
      id: 'addressLine2',
      name: 'Address line 2',
      type: 'Symbol'
    },
    {
      id: 'addressLine3',
      name: 'Address line 3',
      type: 'Symbol'
    },
    {
      id: 'addressLine4',
      name: 'Address line 4',
      type: 'Symbol'
    }
  ]
}

export const EventSpeaker = {
  contentType: 'EventSpeaker',
  fields: [
    {
      id: 'name',
      name: 'Speaker name',
      type: 'Symbol'
    },
    {
      id: 'role',
      name: 'Speaker role',
      type: 'Symbol'
    },
    {
      id: 'description',
      name: 'Description',
      type: 'Text'
    },
    {
      id: 'speakerImage',
      name: 'Speaker image',
      type: 'ModuleMedia'
    }
  ]
}

export const EventBlockSpeakers = {
  contentType: 'EventBlockSpeakers',
  fields: [
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol',
      required: true
    },
    {
      id: 'description',
      name: 'Description',
      type: 'RichText'
    },
    {
      id: 'eventSpeakers',
      name: 'Event speakers',
      type: 'Array',
      subType: 'EventSpeaker'
    }
  ]
}

// Block

export const EventFormBlock = {
  contentType: 'EventFormBlock',
  fields: [
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol'
    },
    {
      id: 'description',
      name: 'Description',
      type: 'RichText'
    },
    {
      id: 'eventAgendaOrLocationSection',
      name: 'Event agenda or location section',
      type: ['EventAgendaSection', 'EventLocation']
    }
  ]
}

export const EventLogistics = {
  contentType: 'EventLogistics',
  fields: [
    {
      id: 'title',
      name: 'Title',
      type: 'Symbol'
    },
    {
      id: 'description',
      name: 'Description',
      type: 'RichText'
    },
    {
      id: 'image',
      name: 'Image',
      type: 'ModuleMedia',
      imageOptions: {
        sizes: {
          small: {
            width: 327,
            height: 436,
            aspectRatio: '3/4'
          },
          medium: {
            width: 312,
            height: 416,
            aspectRatio: '3/4'
          },
          large: {
            width: 576,
            height: 768,
            aspectRatio: '3/4'
          }
        },
        lazy: true
      }
    },
    {
      id: 'map',
      name: 'Google maps',
      type: 'Location'
    },
    {
      id: 'eventAgendaOrLocationSection',
      name: 'Event agenda or location section',
      type: ['EventAgendaSection', 'EventLocation']
    }
  ]
}

export const PageEventsDetail = {
  contentType: 'PageEventsDetail',
  fields: [
    {
      id: 'logo',
      name: 'Logo',
      type: 'ModuleMedia'
    },
    {
      id: 'eventName',
      name: 'Event name',
      type: 'Symbol',
      required: true
    },
    {
      id: 'eventDescription',
      name: 'Event description',
      type: 'RichText'
    },
    {
      id: 'eyebrow',
      name: 'Eyebrow',
      type: 'Symbol'
    },
    {
      id: 'heroTheme',
      name: 'Hero Theme',
      type: 'GlobalDataTheme'
    },
    {
      id: 'headerImage',
      name: 'Header image',
      type: 'ModuleMedia',
      imageOptions: [
        // Vertical
        {
          sizes: imageSizes.columns6_34
        },
        // Horizontal
        {
          sizes: {
            small: {
              width: 327,
              height: 436,
              aspectRatio: '3/4'
            },
            medium: {
              width: 672,
              height: 269,
              aspectRatio: '5/2'
            },
            large: {
              width: 1200,
              height: 480,
              aspectRatio: '5/2'
            }
          }
        }
      ]
    },
    {
      id: 'date',
      name: 'Event date',
      type: 'Date',
      required: true
    },
    {
      id: 'endDate',
      name: 'Event end date',
      type: 'Date'
    },
    {
      id: 'registrationDeadline',
      name: 'Registration deadline',
      type: 'Date',
      required: true
    },
    {
      id: 'location',
      name: 'Event location',
      type: 'EventLocation'
    },
    {
      id: 'formId',
      name: 'Form id',
      type: 'Symbol'
    },
    {
      id: 'headerLayout',
      name: 'Header layout',
      type: 'Symbol',
      localized: false,
      // Validation names are changed for readability - mapped in the page
      validations: [
        {
          in: [
            'Header with form',
            'Header with fullscreen image',
            'Header with half screen image',
            'Header with only title'
          ]
        }
      ],
      defaultValue: 'WITH_FORM'
    },
    {
      id: 'eventSections1',
      name: 'Event sections 1',
      type: 'Array',
      subType: [
        'BlockLandingUspList',
        'BlockLandingMediaAndText',
        'BlockLandscapeMediaThenContent',
        'BlockContentCenteredTextOnly',
        'EventBlockSpeakers',
        'BlockLogoBar',
        'BlockSimpleQuote',
        'EventBlockAgenda',
        'BlockCampaignsCallToAction',
        'BlockLandingLogoBar',
        'BlockCampaignsMedia'
      ],
      lazy: true,
      validations: [{ size: { min: 0, max: 10 } }]
    },
    {
      id: 'formBlock',
      name: 'Form block',
      type: 'EventFormBlock'
    },
    {
      id: 'logistics',
      name: 'Event logistics',
      type: 'EventLogistics',
      imageOptions: {
        resize: true,
        sizes: {
          small: { width: 327, height: 436, aspectRatio: '3/4' },
          medium: { width: 312, height: 416, aspectRatio: '3/4' },
          large: { width: 576, height: 768, aspectRatio: '3/4' }
        },
        lazy: true
      }
    },
    {
      id: 'eventSections2',
      name: 'Event sections 2',
      type: 'Array',
      subType: [
        'BlockLandingUspList',
        'BlockLandingMediaAndText',
        'BlockLandscapeMediaThenContent',
        'BlockContentCenteredTextOnly',
        'BlockLogoBar',
        'BlockSimpleQuote',
        'BlockCampaignsCallToAction',
        'BlockLandingLogoBar',
        'BlockCampaignsMedia'
      ],
      lazy: true,
      validations: [{ size: { min: 0, max: 2 } }]
    },
    {
      id: 'relatedContent',
      name: 'Related content',
      type: 'BlockRelatedContent'
    }
  ]
}

export default {
  EventAgendaSectionItem,
  EventAgendaSection,
  EventBlockAgenda,
  EventLocation,
  EventSpeaker,
  EventBlockSpeakers,
  EventFormBlock,
  EventLogistics,
  PageEventsDetail
}
